import type { PayeeRule } from '../lib/payee';
import type { MwCockpitRow, MwCost, MwData, MwDeal, MwPayout } from '../lib/mw';
import type {
  Account, CapitalPosition, Category, CategoryMonth, CategoryType, ImportResult, ImportRow, Month,
  InvestmentMove, ProvisionalTransaction, ReviewItem, Transaction, Upload,
} from './types';

/** Targets copied into a new month row. Actuals are never copied. */
export type PlanTemplate = Pick<Month, 'investment_target' | 'personal_spend_cap' | 'lifestyle_spend_cap' | 'liquid_reserve_target'>;

/**
 * Everything the app reads or writes. Two implementations:
 *  - live: Supabase, constructed only outside Demo Mode;
 *  - demo: synthetic in-memory data, read-only, never touches the network.
 */
export interface Repo {
  readonly isDemo: boolean;

  currentUserId(): Promise<string | null>;
  signIn(email: string, password: string): Promise<void>;
  sendMagicLink(email: string): Promise<void>;
  signOut(): Promise<void>;
  onAuthChange(cb: () => void): () => void;

  months(): Promise<Month[]>;
  ensureMonth(monthStart: string, template: PlanTemplate | null): Promise<Month>;
  categories(): Promise<Category[]>;
  ensureCategory(name: string, type: CategoryType): Promise<Category>;
  categoryMonths(monthId: string): Promise<CategoryMonth[]>;
  transactions(monthStart: string, categoryId?: string): Promise<Transaction[]>;
  accounts(): Promise<Account[]>;
  capitalPositions(): Promise<CapitalPosition[]>;
  /** Bank-alert rows for a month. Null when daily alerts are not set up in this database. */
  provisional(monthStart: string): Promise<ProvisionalTransaction[] | null>;

  uploads(monthId: string): Promise<Upload[]>;
  reviewItems(monthStart: string): Promise<ReviewItem[]>;
  uploadStatement(file: File, month: Month): Promise<Upload>;
  downloadStatement(upload: Upload): Promise<Blob>;
  supersedeDuplicates(monthId: string): Promise<number>;
  /** Compare a processed statement's account alerts with its transactions again (alerts that arrived later). */
  matchAlerts(uploadId: string): Promise<number>;
  importStatement(args: { uploadId: string; accountId: string | null; summary: Record<string, unknown>; rows: ImportRow[] }): Promise<ImportResult>;
  markUploadFailed(uploadId: string, error: string): Promise<void>;
  /** Classify one item; with a payee key, remember it and resolve matching open items. */
  resolveReview(reviewId: string, categoryId: string, payeeKey?: string | null): Promise<{ alsoResolved: number }>;
  payeeRules(): Promise<PayeeRule[]>;
  publishMonth(monthId: string): Promise<Month>;

  addQuickCapture(text: string): Promise<void>;

  /** Migrate World tracker. Null when its tables are not in this database yet. */
  mw(): Promise<MwData | null>;
  /** Migrate World debits from statements, on or after a date. */
  mwCockpit(from: string): Promise<MwCockpitRow[]>;
  addMwCost(c: Omit<MwCost, 'id' | 'source'>): Promise<void>;
  addMwPayout(p: Omit<MwPayout, 'id'>): Promise<void>;
  saveMwDeal(d: Omit<MwDeal, 'id'> & { id?: string }): Promise<void>;
  saveMwRates(usd: number | null, eur: number | null): Promise<void>;

  /** "I moved it" records. Null when the table is not in this database yet. */
  investmentMoves(): Promise<InvestmentMove[] | null>;
  addInvestmentMove(m: Omit<InvestmentMove, 'id'>): Promise<void>;
}

export class DemoReadOnlyError extends Error {
  constructor() { super('Demo Mode is read-only. Nothing was saved.'); }
}

export function errorMessage(e: unknown): string {
  const raw = e instanceof Error ? e.message
    : e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e);
  return friendly(raw);
}

/** Plain-language versions of the technical errors people actually meet. Anything else passes through. */
function friendly(raw: string): string {
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(raw)) return 'Could not reach Finance Cockpit. Check your connection and try again.';
  if (/jwt expired|invalid jwt|refresh token/i.test(raw)) return 'Your sign-in has expired. Sign in again to continue.';
  if (/row-level security|permission denied/i.test(raw)) return 'This account is not allowed to change that record.';
  return raw;
}
